import { useState, useCallback } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { CreateAssetForm } from "./CreateAssetForm";

interface CreateAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssetCreated?: () => void;
  projectId?: string; // If provided, switches to purchase mode
  // Edit mode - if asset provided, we're editing. Reuse the form's asset type so the
  // dialog and form can't drift (the form expects asset_type_id + the LCA fields).
  asset?: React.ComponentProps<typeof CreateAssetForm>["asset"];
}

/**
 * CreateAssetDialog - Fullscreen dialog for creating or editing assets
 *
 * This is a controlled component - you must manage the open state from the parent.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <Button onClick={() => setOpen(true)}>Create Asset</Button>
 *
 * <CreateAssetDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   onAssetCreated={() => console.log('Asset created!')}
 * />
 * ```
 */
export function CreateAssetDialog({
  open,
  onOpenChange,
  onAssetCreated,
  projectId,
  asset,
}: CreateAssetDialogProps) {
  const { t } = useTranslation();
  const isEditMode = !!asset;
  const [submitHandler, setSubmitHandler] = useState<(() => void) | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const isPurchaseMode = !!projectId && !isEditMode;

  const handleSuccess = () => {
    onOpenChange(false);
    onAssetCreated?.();
  };

  const handleSubmitReady = useCallback((handler: () => void, loading: boolean) => {
    setSubmitHandler(() => handler);
    setIsSubmitting(loading);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitHandler) {
      submitHandler();
    }
  };

  // Mode-specific copy in one place (edit > purchase > create precedence).
  const mode = isEditMode ? "edit" : isPurchaseMode ? "purchase" : "create";
  const labels = {
    edit: {
      title: t("things.edit"),
      submit: t("things.update"),
      submitting: t("things.form.submitting.updating"),
      description: t("things.form.descriptions.edit"),
    },
    purchase: {
      title: t("things.addPurchase"),
      submit: t("things.addPurchaseButton"),
      submitting: t("things.form.submitting.purchasing"),
      description: t("things.form.descriptions.purchase"),
    },
    create: {
      title: t("things.addNew"),
      submit: t("things.add"),
      submitting: t("things.form.submitting.adding"),
      description: t("things.form.descriptions.create"),
    },
  }[mode];

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={labels.title}
      description={labels.description}
      onSubmit={handleSubmit}
      submitLabel={labels.submit}
      submittingLabel={labels.submitting}
      isSubmitting={isSubmitting}
      skipCloseConfirmation={!isDirty}
      formId="create-asset-form"
    >
      <CreateAssetForm
        onSuccess={handleSuccess}
        onSubmitReady={handleSubmitReady}
        onDirtyChange={setIsDirty}
        projectId={projectId}
        asset={asset}
      />
    </FullscreenFormDialog>
  );
}
